import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ChevronLeft, Eye, Building2, FolderOpen } from 'lucide-react'
import { useState, useCallback } from 'react'

import { DocumentViewer } from '@/components/knowledge-base/document-viewer'
import { Editor } from '@/components/knowledge-base/editor'
import { TemplateSelector } from '@/components/knowledge-base/template-selector'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/toast'
import {
  useConfirmDialog,
  useDocument,
  useUpdateDocument,
  useDeleteDocument,
  useCreateDocument,
  useKnowledgeBases,
} from '@/hooks'

export interface SearchParams {
  knowledgeBaseId?: string
}

export const Route = createFileRoute('/_authenticated/knowledge-base/documents/$documentId')({
  component: DocumentPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    knowledgeBaseId: typeof search.knowledgeBaseId === 'string' ? search.knowledgeBaseId : undefined,
  }),
})

/**
 * 文档页面
 * 支持查看、编辑和创建文档
 */
function DocumentPage() {
  const params = Route.useParams()
  const documentId = params.documentId
  const search = Route.useSearch()
  const searchKnowledgeBaseId = search.knowledgeBaseId
  const navigate = useNavigate()
  const isNewDocument = documentId === 'new'

  const { data: document, isLoading } = useDocument(isNewDocument ? '' : documentId)
  const updateDocument = useUpdateDocument()
  const deleteDocument = useDeleteDocument()
  const createDocument = useCreateDocument()

  const [isEditing, setIsEditing] = useState(isNewDocument)
  const [showTemplateSelector, setShowTemplateSelector] = useState(isNewDocument)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>()
  const [newDocKnowledgeBaseId, setNewDocKnowledgeBaseId] = useState<string>(searchKnowledgeBaseId ?? '')
  const [showVersionHistory, setShowVersionHistory] = useState(false)
  const { confirmWithPreset, ConfirmDialog, setLoading } = useConfirmDialog()
  const toast = useToast()

  const handleBack = () => {
    if (document?.knowledgeBaseId) {
      void navigate({ to: '/knowledge-base/spaces/$spaceId', params: { spaceId: document.knowledgeBaseId } })
    } else if (newDocKnowledgeBaseId) {
      void navigate({ to: '/knowledge-base/spaces/$spaceId', params: { spaceId: newDocKnowledgeBaseId } })
    } else {
      void navigate({ to: '/knowledge-base' })
    }
  }

  const handleEdit = () => {
    setIsEditing(true)
  }

  const handleCancelEdit = () => {
    if (isNewDocument) {
      handleBack()
    } else {
      setIsEditing(false)
    }
  }

  const handleSave = useCallback(
    (title: string, content: string) => {
      if (isNewDocument) {
        // 创建新文档
        if (!newDocKnowledgeBaseId) {
          toast.error('请选择一个知识库')
          return
        }
        createDocument.mutate(
          {
            knowledgeBaseId: newDocKnowledgeBaseId,
            title,
            content,
            templateId: selectedTemplateId,
          },
          {
            onSuccess: (newDoc) => {
              void navigate({
                to: '/knowledge-base/documents/$documentId',
                params: { documentId: newDoc.id },
              })
            },
          }
        )
      } else {
        // 更新现有文档
        updateDocument.mutate(
          {
            id: documentId,
            data: { title, content },
          },
          {
            onSuccess: () => {
              setIsEditing(false)
            },
          }
        )
      }
    },
    [isNewDocument, newDocKnowledgeBaseId, selectedTemplateId, documentId, createDocument, updateDocument, navigate]
  )

  const handleDelete = useCallback(() => {
    if (!document) { return }
    void (async () => {
      const confirmed = await confirmWithPreset(
        'delete',
        `确定要删除文档 "${document.title}" 吗？此操作不可撤销。`
      )
      if (confirmed) {
        setLoading(true)
        try {
          await deleteDocument.mutateAsync(documentId)
          void navigate({ to: '/knowledge-base/spaces/$spaceId', params: { spaceId: document.knowledgeBaseId } })
        } finally {
          setLoading(false)
        }
      }
    })()
  }, [document, documentId, deleteDocument, navigate, confirmWithPreset, setLoading])

  // handleExport 已移除 - 未在 UI 中使用

  // New document - show template selector
  if (isNewDocument && showTemplateSelector) {
    return (
      <div className="flex h-full flex-col">
        <div className="border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold">创建新文档</h1>
              <p className="text-sm text-muted-foreground mt-1">选择一个模板开始</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleBack}>
                取消
              </Button>
              <Button
                onClick={() => setShowTemplateSelector(false)}
                disabled={!newDocKnowledgeBaseId}
              >
                继续
              </Button>
            </div>
          </div>

          {/* Knowledge Base Selector for new document */}
          <div className="mt-4">
            <label className="text-sm font-medium">选择知识库</label>
            <KnowledgeBaseSelector
              value={newDocKnowledgeBaseId}
              onChange={setNewDocKnowledgeBaseId}
            />
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          <TemplateSelector
            knowledgeBaseId={newDocKnowledgeBaseId}
            selectedTemplateId={selectedTemplateId}
            onSelect={(template) => setSelectedTemplateId(template?.id)}
          />
        </div>
      </div>
    )
  }

  // Loading state
  if (!isNewDocument && isLoading) {
    return <DocumentPageSkeleton />
  }

  // Document not found
  if (!isNewDocument && !document) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <h2 className="text-lg font-medium">文档不存在</h2>
          <p className="text-sm text-muted-foreground mt-1">该文档可能已被删除或您没有访问权限</p>
          <Button variant="outline" className="mt-4" onClick={handleBack}>
            <ChevronLeft className="mr-2 h-4 w-4" />
            返回
          </Button>
        </div>
      </div>
    )
  }

  // Editing mode
  if (isEditing) {
    return (
      <div className="flex h-full flex-col">
        <div className="border-b px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handleCancelEdit}>
              <ChevronLeft className="mr-2 h-4 w-4" />
              取消
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)}>
              <Eye className="mr-2 h-4 w-4" />
              预览
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-hidden">
          <Editor
            initialTitle={document?.title ?? ''}
            initialContent={document?.content ?? ''}
            documentId={isNewDocument ? undefined : documentId}
            knowledgeBaseId={document?.knowledgeBaseId ?? newDocKnowledgeBaseId}
            onSave={handleSave}
            isSaving={updateDocument.isPending || createDocument.isPending}
            autoSave={!isNewDocument}
          />
        </div>
      </div>
    )
  }

  // View mode
  return (
    <>
      <DocumentViewer
        documentId={documentId}
        onEdit={handleEdit}
        onDelete={handleDelete}
        showVersionHistory={showVersionHistory}
        onVersionHistoryChange={setShowVersionHistory}
      />
      <ConfirmDialog />
    </>
  )
}

// ==================== Sub Components ====================

interface KnowledgeBaseSelectorProps {
  value: string
  onChange: (value: string) => void
}

function KnowledgeBaseSelector({ value, onChange }: KnowledgeBaseSelectorProps) {
  const { data: knowledgeBases } = useKnowledgeBases()

  const companyKB = knowledgeBases?.find((kb) => kb.type === 'company')
  const projectKBs = knowledgeBases?.filter((kb) => kb.type === 'project') ?? []

  return (
    <Select
      value={value || 'placeholder'}
      onValueChange={(val) => onChange(val === 'placeholder' ? '' : val)}
    >
      <SelectTrigger className="mt-1 w-full">
        <SelectValue placeholder="选择知识库..." />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="placeholder" disabled>选择知识库...</SelectItem>
        {companyKB && (
          <SelectItem key={companyKB.id} value={companyKB.id}>
            <span className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              {companyKB.name}
            </span>
          </SelectItem>
        )}
        {projectKBs.map((kb) => (
          <SelectItem key={kb.id} value={kb.id}>
            <span className="flex items-center gap-2">
              <FolderOpen className="h-4 w-4" />
              {kb.name}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function DocumentPageSkeleton() {
  return (
    <div className="flex h-full flex-col">
      <div className="border-b px-4 py-3">
        <Skeleton className="h-4 w-48" />
        <div className="mt-3 flex items-start justify-between">
          <div>
            <Skeleton className="h-8 w-64" />
            <div className="mt-2 flex gap-4">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-9 w-20" />
            <Skeleton className="h-9 w-16" />
          </div>
        </div>
      </div>
      <div className="flex-1 p-6">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4 mt-2" />
        <Skeleton className="h-4 w-5/6 mt-2" />
        <Skeleton className="h-4 w-full mt-4" />
        <Skeleton className="h-4 w-2/3 mt-2" />
      </div>
    </div>
  )
}
